/**
 * Minimal ZIP reader used by local RoQeet import paths.
 *
 * Supported scope is intentionally narrow: central-directory ZIP parsing,
 * stored entries, raw-deflate entries when `DecompressionStream` is available,
 * and no ZIP64 support. This is not a general-purpose archive library.
 */

const EOCD_SIG = 0x06054b50; // End of Central Directory
const CDH_SIG = 0x02014b50; // Central Directory Header
const LFH_SIG = 0x04034b50; // Local File Header

export interface ZipEntry {
  name: string;
  compressionMethod: number; // 0 = stored, 8 = deflate
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

export interface ReadZipEntryDataOptions {
  maxBytes?: number;
}

export class ZipEntrySizeLimitError extends Error {
  constructor(readonly maxBytes: number) {
    super(`ZIP entry exceeds maximum size of ${maxBytes} bytes.`);
    this.name = "ZipEntrySizeLimitError";
  }
}

export function readZipEntries(bytes: Uint8Array): ZipEntry[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  let eocd = -1;
  const minEocd = 22;
  for (let i = bytes.length - minEocd; i >= 0; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return [];

  const cdCount = view.getUint16(eocd + 10, true);
  const cdOffset = view.getUint32(eocd + 16, true);

  const entries: ZipEntry[] = [];
  let p = cdOffset;
  const dec = new TextDecoder("utf-8");

  for (let n = 0; n < cdCount; n++) {
    if (p + 46 > bytes.length) break;
    if (view.getUint32(p, true) !== CDH_SIG) break;

    const method = view.getUint16(p + 10, true);
    const compressedSize = view.getUint32(p + 20, true);
    const uncompressedSize = view.getUint32(p + 24, true);
    const fnLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localHeaderOffset = view.getUint32(p + 42, true);
    const name = dec.decode(bytes.subarray(p + 46, p + 46 + fnLen));

    entries.push({
      name,
      compressionMethod: method,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });

    p += 46 + fnLen + extraLen + commentLen;
  }

  return entries;
}

async function inflateRaw(data: Uint8Array, maxBytes?: number): Promise<Uint8Array | undefined> {
  const DS = (globalThis as { DecompressionStream?: typeof DecompressionStream })
    .DecompressionStream;
  if (typeof DS !== "function") return undefined;
  try {
    const buf = new Uint8Array(data.byteLength);
    buf.set(data);
    const stream = new Blob([buf]).stream().pipeThrough(new DS("deflate-raw"));
    return readStreamWithLimit(stream, maxBytes);
  } catch (error) {
    if (error instanceof ZipEntrySizeLimitError) throw error;
    return undefined;
  }
}

async function readStreamWithLimit(
  stream: ReadableStream<Uint8Array>,
  maxBytes: number | undefined,
): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      total += value.byteLength;
      if (maxBytes !== undefined && total > maxBytes) {
        await reader.cancel();
        throw new ZipEntrySizeLimitError(maxBytes);
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

function assertWithinLimit(byteLength: number, maxBytes: number | undefined): void {
  if (maxBytes === undefined || byteLength <= maxBytes) return;
  throw new ZipEntrySizeLimitError(maxBytes);
}

export async function readZipEntryData(
  bytes: Uint8Array,
  entry: ZipEntry,
  options: ReadZipEntryDataOptions = {},
): Promise<Uint8Array | undefined> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const o = entry.localHeaderOffset;
  if (o + 30 > bytes.length || view.getUint32(o, true) !== LFH_SIG) return undefined;

  const fnLen = view.getUint16(o + 26, true);
  const extraLen = view.getUint16(o + 28, true);
  const dataStart = o + 30 + fnLen + extraLen;
  const dataEnd = dataStart + entry.compressedSize;
  if (dataEnd > bytes.length) return undefined;

  const slice = bytes.subarray(dataStart, dataEnd);
  if (entry.compressionMethod === 0) {
    assertWithinLimit(slice.byteLength, options.maxBytes);
    return slice.slice();
  }
  if (entry.compressionMethod === 8) {
    return inflateRaw(slice, options.maxBytes);
  }
  return undefined;
}

export async function readZipTextFile(
  bytes: Uint8Array,
  name: string,
): Promise<string | undefined> {
  const entries = readZipEntries(bytes);
  const entry = entries.find((e) => e.name === name || e.name.endsWith(`/${name}`));
  if (!entry) return undefined;
  const data = await readZipEntryData(bytes, entry);
  if (!data) return undefined;
  return new TextDecoder("utf-8").decode(data);
}

export function isZip(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  return bytes[0] === 0x50 && bytes[1] === 0x4b && (bytes[2] === 0x03 || bytes[2] === 0x05);
}
